export const MOCKUP_IMAGE_ERRORS = {
  UNREADABLE: "MOCKUP_IMAGE_UNREADABLE",
  TOO_LARGE: "MOCKUP_IMAGE_TOO_LARGE",
};

const KEPT_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 4.5 * 1024 * 1024;
const MAX_SIDE = 7990;
const MIN_SIDE = 320;
const SHRINK_STEP = 0.8;
const JPEG_QUALITIES = [0.92, 0.85, 0.75, 0.6];
const BACKGROUND = "#ffffff";
const TYPES = { PNG: "image/png", JPEG: "image/jpeg", SVG: "image/svg+xml" };

const readAsBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(MOCKUP_IMAGE_ERRORS.UNREADABLE));
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.readAsDataURL(blob);
  });

const standaloneSvgOf = async (file) => {
  const root = new DOMParser()
    .parseFromString(await file.text(), "text/html")
    .querySelector("svg");
  if (!root) return file;
  return new Blob([new XMLSerializer().serializeToString(root)], {
    type: TYPES.SVG,
  });
};

const readableOf = (file) =>
  file.type === TYPES.SVG ? standaloneSvgOf(file) : Promise.resolve(file);

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(MOCKUP_IMAGE_ERRORS.UNREADABLE));
    };
    image.src = url;
  });

const toBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const draw = (image, scale) => {
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.fillStyle = BACKGROUND;
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  return canvas;
};

const encode = async (canvas) => {
  if (!canvas) return null;
  const png = await toBlob(canvas, TYPES.PNG);
  if (png && png.size <= MAX_BYTES) return png;
  for (const quality of JPEG_QUALITIES) {
    const jpeg = await toBlob(canvas, TYPES.JPEG, quality);
    if (jpeg && jpeg.size <= MAX_BYTES) return jpeg;
  }
  return null;
};

const fitsAsIs = (file, image) =>
  KEPT_TYPES.includes(file.type) &&
  file.size <= MAX_BYTES &&
  image.naturalWidth <= MAX_SIDE &&
  image.naturalHeight <= MAX_SIDE;

export const prepareMockupImage = async (file) => {
  const image = await loadImage(await readableOf(file));
  const longSide = Math.max(image.naturalWidth, image.naturalHeight);
  if (!longSide) throw new Error(MOCKUP_IMAGE_ERRORS.UNREADABLE);
  if (fitsAsIs(file, image)) {
    return { data: await readAsBase64(file), mediaType: file.type };
  }
  let scale = Math.min(1, MAX_SIDE / longSide);
  while (longSide * scale >= MIN_SIDE) {
    const blob = await encode(draw(image, scale));
    if (blob) return { data: await readAsBase64(blob), mediaType: blob.type };
    scale *= SHRINK_STEP;
  }
  throw new Error(MOCKUP_IMAGE_ERRORS.TOO_LARGE);
};

export const preparePictureImage = (file) => {
  if (file.type !== TYPES.SVG) return prepareMockupImage(file);
  if (file.size > MAX_BYTES) {
    return Promise.reject(new Error(MOCKUP_IMAGE_ERRORS.TOO_LARGE));
  }
  return readAsBase64(file).then((data) => ({
    data,
    mediaType: TYPES.SVG,
  }));
};
